/* eslint-disable camelcase */
import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { getAllExecutedPortfolio, getCandidatTransactions } from '../../data/store/account/accountActions';

import { Button, Col, Row, Spin } from 'antd';
import CustomSearchInput from '../../components/CustomSearchInput/CustomSearchInput';

import { ReactComponent as Filter } from '../../assets/filter-icon.svg';
import { ReactComponent as Eye } from '../../assets/eye-icon.svg';
import { ReactComponent as Download } from '../../assets/export-icon.svg';
import { ReactComponent as Delete } from '../../assets/delete-black-icon.svg';

import dayjs from 'dayjs';
import ModalFilterCandidat from './ModalFilterCandidat';
import ModalViewTransaction from './ModalViewTransaction';
import { textSort } from '../../helpers/textSort';
import ModalImplemente from './ModalImplemente';
import ModalDownloadReport from './ModalDownloadReport';
import { getCurrency } from '../../data/store/instruments/instrumentsActions';
import CustomSortIcon from '../../components/CustomSortIcon/CustomSortIcon';
import CustomTable from '../../components/CustomTable/CustomTable';
import ModalDeleteCandidat from './ModalDeleteCandidat';
import ModalRevertCandidat from './ModalRevertCandidat';

const AdminExecutedTable = () => {
  const { t } = useTranslation('');
  const dispatch = useDispatch();

  const { executed, isLoading, isDownload } = useSelector((state) => state.accountReducer);
  const advisors = useSelector((state) => state.advisorReducer.users.list);
  const accounts = useSelector((state) => state.accountReducer?.accounts?.list);

  const [tableColumns, setTableColumns] = useState();
  const [filteredPortfolio, setFilteredPortfolio] = useState([]);
  const [filter, setFilter] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [filterCounter, setFilterCounter] = useState(null);
  const [openModalTransaction, setOpenModalTransaction] = useState(false);
  const [openModalImplemente, setOpenModalImplemente] = useState(null);
  const [openModalDownloadReport, setOpenModalDownloadReport] = useState(null);
  
  const [modalDeleteCandidat, setModalDeleteCandidat] = useState(null);
  const [modalRevertCandidat, setModalRevertCandidat] = useState(null);

  useEffect(() => {
    dispatch(getAllExecutedPortfolio());
    dispatch(getCurrency());
    return () => {
      dispatch({ type: 'CLEAR_EXECUTED_PORTFOLIO' });
    };
  }, []);

  const applyFilters = () => {
    if (!filter) return setFilteredPortfolio(executed);
    const { ...rest } = filter;
    // By created
    const filteredByDate = executed.filter(item => {
      const createDate = dayjs(item.create_date);
      if (!rest.create_start_date && !rest.create_end_date) {
        return true;
      }
      if (!rest.create_start_date) {
        return createDate.isBefore(rest.create_end_date, 'day') || createDate.isSame(rest.create_end_date, 'day');
      }
      if (!rest.create_end_date) {
        return createDate.isAfter(rest.create_start_date, 'day') || createDate.isSame(rest.create_start_date, 'day');
      }
      return (createDate.isAfter(rest.create_start_date, 'day') || createDate.isSame(rest.create_start_date, 'day')) &&
              (createDate.isBefore(rest.create_end_date, 'day') || createDate.isSame(rest.create_end_date, 'day'));
    });
    // By approved
    const filteredByApproved = filteredByDate.filter(item => {
      const approvedDate = dayjs(item.approve_date);
      if (!rest.approved_start_date && !rest.approved_end_date) {
        return true;
      }
      if (!rest.approved_start_date) {
        return approvedDate.isBefore(rest.approved_end_date, 'day') || approvedDate.isSame(rest.approved_end_date, 'day');
      }
      if (!rest.create_end_date) {
        return approvedDate.isAfter(rest.approved_start_date, 'day') || approvedDate.isSame(rest.approved_start_date, 'day');
      }
      return (approvedDate.isAfter(rest.approved_start_date, 'day') || approvedDate.isSame(rest.approved_start_date, 'day')) &&
              (approvedDate.isBefore(rest.approved_end_date, 'day') || approvedDate.isSame(rest.approved_end_date, 'day'));
    });

    const filteredByExecuted = filteredByApproved.filter(item => {
      const executedDate = dayjs(item.execute_date);
      if (!rest.executed_start_date && !rest.executed_end_date) {
        return true;
      }
      if (!rest.executed_start_date) {
        return executedDate.isBefore(rest.executed_end_date, 'day') || executedDate.isSame(rest.executed_end_date, 'day');
      }
      if (!rest.executed_end_date) {
        return executedDate.isAfter(rest.executed_start_date, 'day') || executedDate.isSame(rest.executed_start_date, 'day');
      }
      return (executedDate.isAfter(rest.executed_start_date, 'day') || executedDate.isSame(rest.executed_start_date, 'day')) &&
              (executedDate.isBefore(rest.executed_end_date, 'day') || executedDate.isSame(rest.executed_end_date, 'day'));
    });

    let filteredByPortfolio = [];
    if (rest.portfolioList[0] === 'All') {
      filteredByPortfolio = filteredByExecuted;
    } else {
      filteredByPortfolio = filteredByExecuted.filter(i => rest.portfolioList.includes(i.name));
    }

    let filteredByAdvisor = [];
    if (rest.advisorsList[0] === 'All') {
      filteredByAdvisor = filteredByPortfolio;
    } else {
      filteredByAdvisor = filteredByPortfolio.filter(i => rest.advisorsList.includes(i.advisor_id));
    }

    let filteredByClient = [];
    if (rest.clientsList[0] === 'All') {
      filteredByClient = filteredByAdvisor;
    } else {
      filteredByClient = filteredByAdvisor.filter(i => rest.clientsList.includes(i.client_id));
    }
    setFilteredPortfolio(filteredByClient);
  };

  useEffect(() => {
    applyFilters();
  }, [filter]);

  useEffect(() => {
    if (executed) {
      setFilteredPortfolio(executed);
    } else {
      setFilteredPortfolio([]);
    }
  }, [executed]);

  const getAdvisorName = (id) => {
    const advisor = advisors?.find(i => i.ID === id);
    const firstName = advisor?.profile?.firstName ?? '';
    const lastName = advisor?.profile?.lastName ?? '';
    return firstName || lastName ? `${firstName} ${lastName}` : 'Super Admin';
  };

  const getClientName = (id) => {
    const account = accounts?.find(i => i.id === id);
    const name = account?.name ?? '';
    return name;
  };

  const handlImplemente = (id, portfolio_id) => {
    console.log('🚀 ~ handlImplemente ~ portfolio_id:', portfolio_id);
    setOpenModalImplemente(id);
  };

  const closeModalImplemente = () => {
    setOpenModalImplemente(null);
  };
  
  const hendleDownload = (portfolio_id, client_id) => {
    setOpenModalDownloadReport({portfolio_id, client_id});
  };

  const closeModalDownloadReport = () => {
    setOpenModalDownloadReport(null);
  };

  const handleRevert =(id) => {
    setModalRevertCandidat(id);
  };

  const handleDelete =(id) => {
    setModalDeleteCandidat(id);
  };

  useEffect(() => {
    const defaultColumns = [
      {
        title: t('ADVISOR'),
        dataIndex: 'advisor_id',
        className: 'suggestions-name',
        align: 'left',
        render: (value) => getAdvisorName(value),
        sorter: (a, b) => textSort(getAdvisorName(a), getAdvisorName(b)),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('CLIENT'),
        dataIndex: 'client_id',
        className: 'suggestions-name',
        align: 'left',
        render: (value) => getClientName(value),
        sorter: (a, b) => textSort(getClientName(a), getClientName(b)),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('PORTFOLIO_NUMBER'),
        dataIndex: 'portfolio_code',
        className: 'suggestions-name',
        align: 'left',
        sorter: (a, b) => textSort(a.name, b.name),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('CREATION_DATE'),
        dataIndex: 'create_date',
        className: 'suggestions-create-date',
        align: 'left',
        render: (value) => <><span className='create-date'>{dayjs(value).format('MMM DD  YYYY')}</span><br/>
          <span className='suggestions-create-time'>{dayjs(value).format('HH:mm A')}</span></>,
        sorter: (a, b) => textSort(a.create_date, b.create_date),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('APPROVAL_DATE'),
        dataIndex: 'approve_date',
        className: 'suggestions-approval-date',
        align: 'left',
        render: (value) => <><span className='create-date'>{dayjs(value).format('MMM DD  YYYY')}</span><br/>
          <span className='suggestions-create-time'>{dayjs(value).format('HH:mm A')}</span></>,
        sorter: (a, b) => textSort(a.approve_date, b.approve_date),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('EXECUTION_DATE'),
        dataIndex: 'execute_date',
        className: 'suggestions-approval-date',
        align: 'left',
        render: (value) => <><span className='create-date'>{dayjs(value).format('MMM DD  YYYY')}</span><br/>
          <span className='suggestions-create-time'>{dayjs(value).format('HH:mm A')}</span></>,
        sorter: (a, b) => textSort(a.execute_date, b.execute_date),
        sortIcon: (props) => <CustomSortIcon {...props} />,
      },
      {
        title: t('ACTIONS'),
        align: 'center',
        dataIndex: 'operation',
        render: (_, record) => {
          return (
            <div className="cell-button-wrapper">
              <Button
                className="action-btn approve"
                onClick={() => handlImplemente(record.id, record.portfolio_id)}
              >
                {t('EXECUTED')}
              </Button>
              <Button className="action-btn approve" onClick={() => handleRevert(record.id)}>
                {t('REVERT')}
              </Button>
              <Button type="text" onClick={() => hendlePreview(record.id, record.portfolio_id)}>
                <Eye />
              </Button>
              {!isDownload
                ? <Button type="text" onClick={() => hendleDownload(record.portfolio_id, record.id)}>
                    <Download />
                  </Button>
                : <Spin size="small" />
              }
              <Button type="text" onClick={() => handleDelete(record.id)}>
                <Delete />
              </Button>
            </div>
          );
        },
      },
    ];
    setTableColumns(defaultColumns);
  }, [executed, advisors, accounts, isDownload]);

  const handeleFilter = () => {
    setOpenFilter(true);
  };

  const handleCloseFilter = () => {
    setOpenFilter(false);
  };

  const handleSearch = (event) => {
    const { value } = event.target;
    if (value?.length > 2) {
      const searched = executed?.filter(
        (i) =>
          i.name?.toLowerCase().includes(value?.toLowerCase())
      );
      setFilteredPortfolio(searched);
    } else {
      setFilteredPortfolio(executed);
    }
  };

  const getCandidatById = useMemo(() => {
    return executed?.find(i => i.id === modalDeleteCandidat || i.id === modalRevertCandidat);
  }, [executed, modalDeleteCandidat, modalRevertCandidat]);

  const hendlePreview = (candidatId, portfolio_id) => {
    setOpenModalTransaction(true);
    dispatch(getCandidatTransactions({ candidatId, portfolio_id }));
  };

  const closeModalTransaction = () => {
    setOpenModalTransaction(false);
  };

  return (
    <div>
      <Row justify={'end'} className='search-wrapper'>
        <Col span={12} className="instrument-button-wraper">
          <CustomSearchInput
            className='account-select-search suggested'
            placeholder={t('SEARCH')}
            onChange={handleSearch}
          />
          <Button
            className="filter-btn"
            type="default"
            disabled={isLoading}
            onClick={handeleFilter}
            icon={<Filter />}
            iconPosition="end"
          >
            {t('FILTER')}
            {filterCounter
              ? (
              <span className="filter-counter">{filterCounter}</span>
                )
              : (
                  ''
                )}
          </Button>
        </Col>
      </Row>
      <div className="client portfolio-table suggestions-table">
          <CustomTable
            rowKey={(record) => record.id}
            loading={isLoading}
            columns={tableColumns}
            dataSource={filteredPortfolio}
            size="middle"
            pagination={false}
          />
      </div>
      <ModalViewTransaction
        openModalTransaction={openModalTransaction}
        closeModalTransaction={closeModalTransaction}
      />
      <ModalFilterCandidat
        type='admin-executed'
        source={executed}
        setFilter={setFilter}
        openFilter={openFilter}
        handleCloseFilter={handleCloseFilter}
        getAdvisorName={getAdvisorName}
        getClientName={getClientName}
      />
      <ModalImplemente
        openModalImplemente={openModalImplemente}
        closeModalImplemente={closeModalImplemente}
        executed={executed}

      />
      {
        openModalDownloadReport &&
        <ModalDownloadReport
        openModalDownloadReport={openModalDownloadReport}
        closeModalDownloadReport={closeModalDownloadReport}
      />}
      {modalDeleteCandidat &&
        <ModalDeleteCandidat
          text='DELETE_THIS_APPROVED'
          candidatType={'admin-executed'}
          userId={getCandidatById?.client_id}
          name={getCandidatById?.name ?? ''}
          modalDeleteCandidat={modalDeleteCandidat}
          setModalDeleteCandidat={setModalDeleteCandidat}
        />}
      {modalRevertCandidat &&
        <ModalRevertCandidat
          candidatType={'admin-approved'}
          userId={getCandidatById?.client_id}
          name={getCandidatById?.name ?? ''}
          modalRevertCandidat={modalRevertCandidat}
          setModalRevertCandidat={setModalRevertCandidat}
        />}
    </div>
  );
};

export default AdminExecutedTable;

AdminExecutedTable.propTypes = {
  data: PropTypes.array,
};
